import React, { useEffect, useState, useMemo } from 'react';
import { getAllCategories } from '../api/categoryApi';
import {
  getProductsByCategory,
  updateProduct,
  deleteProduct,
  uploadProductImages,
  uploadProductImage,
  getAllCompanies,
  getCustomCompaniesLocal,
  saveCustomCompanyLocal,
  getCompanyLogoMapLocal,
  getCompanyLogoLocal,
  saveCompanyLogoLocal,
} from '../api/productApi';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';
import ManageCompaniesModal from './ManageCompaniesModal';
import './AdminDashboard.css';
import { resolveImageUrl } from '../utils/image';
import ProductVideoModal from './ProductVideoModal';
import { isValidYouTubeUrl, getYouTubeVideoId } from '../utils/youtube';

export default function AdminProductPage() {
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>('ALL');
  const [companies, setCompanies] = useState<string[]>([]);
  const [companyLogos, setCompanyLogos] = useState<Record<string, string>>({});
  const [showManageCompanies, setShowManageCompanies] = useState<boolean>(false);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editProduct, setEditProduct] = useState<any>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [videoModal, setVideoModal] = useState<{ isOpen: boolean; videoUrl: string; productName: string }>({
    isOpen: false,
    videoUrl: '',
    productName: '',
  });
  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      const response = await getAllCategories();
      const data = Array.isArray(response) ? response : response.value;
      const list = data || [];
      setCategories(list);
      if (list.length > 0 && selectedCategory === null) {
        setSelectedCategory(list[0].categoryID);
      }
    } catch {
      setCategories([]);
    }
  };

  const fetchCompaniesList = async () => {
    try {
      const apiCompanies = await getAllCompanies();
      const localCompanies = getCustomCompaniesLocal();
      const logosMap = { ...getCompanyLogoMapLocal() };
      apiCompanies.forEach(c => {
        if (c.companyName && c.companyLogoUrl) {
          logosMap[c.companyName.trim().toLowerCase()] = c.companyLogoUrl;
        }
      });
      setCompanyLogos(logosMap);
      const merged = Array.from(
        new Set([
          ...apiCompanies.map(c => c.companyName),
          ...localCompanies,
        ].filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      setCompanies(merged);
    } catch {
      setCompanies(getCustomCompaniesLocal());
      setCompanyLogos(getCompanyLogoMapLocal());
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCompaniesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async (categoryId: number) => {
    setLoading(true);
    try {
      const response = await getProductsByCategory(categoryId);
      const list = response || [];
      setProducts(list);
      // Also merge any companies and logos discovered on loaded products
      const discovered = list
        .map((p: any) => (p.companyName || p.CompanyName || '').trim())
        .filter(Boolean);
      list.forEach((p: any) => {
        const cName = (p.companyName || p.CompanyName || '').trim();
        const cLogo = (p.companyLogoUrl || p.CompanyLogoUrl || '').trim();
        if (cName && cLogo) {
          saveCompanyLogoLocal(cName, cLogo);
        }
      });
      setCompanyLogos(prev => ({ ...prev, ...getCompanyLogoMapLocal() }));
      if (discovered.length > 0) {
        setCompanies(prev => Array.from(new Set([...prev, ...discovered])).sort((a, b) => a.localeCompare(b)));
      }
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      fetchProducts(selectedCategory);
    }
  }, [selectedCategory]);

  const handleEdit = (prod: any) => {
    setEditProduct(prod);
    setShowEdit(true);
  };

  const handleDelete = (productId: number) => {
    setConfirmDeleteId(productId);
  };

  const confirmDelete = async () => {
    if (confirmDeleteId !== null) {
      await deleteProduct(confirmDeleteId);
      setConfirmDeleteId(null);
      if (selectedCategory) fetchProducts(selectedCategory);
      fetchCompaniesList();
    }
  };

  const cancelDelete = () => setConfirmDeleteId(null);

  const handleUpdate = async (updatedProd: any) => {
    await updateProduct(updatedProd);
    setShowEdit(false);
    setEditProduct(null);
    if (selectedCategory) fetchProducts(selectedCategory);
    fetchCompaniesList();
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCompany !== 'ALL') {
      list = list.filter(p => {
        const comp = (p.companyName || p.CompanyName || '').trim();
        return comp.toLowerCase() === selectedCompany.toLowerCase();
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.productName || '').toLowerCase().includes(q) ||
        (p.productDescription || '').toLowerCase().includes(q) ||
        (p.companyName || p.CompanyName || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const orderA = (a.displayOrder || a.DisplayOrder) && (a.displayOrder || a.DisplayOrder) > 0 ? (a.displayOrder || a.DisplayOrder) : 999999;
      const orderB = (b.displayOrder || b.DisplayOrder) && (b.displayOrder || b.DisplayOrder) > 0 ? (b.displayOrder || b.DisplayOrder) : 999999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.productId || a.ProductId || 0) - (b.productId || b.ProductId || 0);
    });
  }, [products, selectedCompany, searchQuery]);

  return (
    <div className="ad-page-wrapper">
      <AdminNav activeTab="products" />

      <main className="ad-main-content">
        <div className="ad-container">
          {/* Header */}
          <div className="ad-page-header">
            <div>
              <h1 className="ad-page-title">Products Management</h1>
              <p className="ad-page-subtitle">Manage fireworks inventory, company brands, prices, wholesale discounts, and high-res imagery</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="ad-btn-action edit"
                style={{ padding: '10px 16px', fontSize: 13 }}
                onClick={() => setShowManageCompanies(true)}
              >
                🏢 Manage Companies
              </button>
              <button 
                className="ad-btn-primary-action"
                onClick={() => navigate('/admin/add-product')}
              >
                <span>+ Add New Product</span>
              </button>
            </div>
          </div>

          {/* Toolbar: Category Selector, Company Filter & Search */}
          <div className="ad-toolbar-card">
            <div className="ad-category-select-wrap">
              <label>Category:</label>
              <select
                className="ad-select"
                value={selectedCategory ?? ''}
                onChange={e => setSelectedCategory(Number(e.target.value))}
              >
                {categories.map(cat => (
                  <option key={cat.categoryID} value={cat.categoryID}>
                    {cat.categoryName} ({cat.productCount || 0})
                  </option>
                ))}
              </select>
            </div>

            <div className="ad-category-select-wrap">
              <label>Company:</label>
              <select
                className="ad-select"
                value={selectedCompany}
                onChange={e => setSelectedCompany(e.target.value)}
              >
                <option value="ALL">All Companies</option>
                {companies.map((comp, idx) => (
                  <option key={idx} value={comp}>
                    {comp}
                  </option>
                ))}
              </select>
            </div>

            <div className="ad-search-box">
              <span className="ad-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search products or company in this category..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="ad-search-input"
              />
              {searchQuery && (
                <button className="ad-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>
          </div>

          {/* Table / Cards */}
          <div className="ad-table-card">
            {loading ? (
              <div className="ad-loading-box">
                <div className="ad-spinner"></div>
                <p>Loading products...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <>
                {/* Desktop View Table */}
                <div className="ad-desktop-table-container">
                  <table className="ad-data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '70px', textAlign: 'center' }}>Rank</th>
                        <th>Thumbnail</th>
                        <th>Product Name</th>
                        <th>Company / Brand</th>
                        <th>Description</th>
                        <th>Price (₹)</th>
                        <th>In Stock</th>
                        <th>Featured Status</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((prod: any) => {
                        const isMostSale = String(prod.isMostSaleProduct ?? prod.isMostSale ?? prod.IsMostSaleProduct ?? false) === 'true';
                        const firstImage = (prod.imageUrls && prod.imageUrls.length > 0 ? prod.imageUrls[0] : prod.imageUrl) || prod.productImageUrl;
                        const prodCompany = prod.companyName || prod.CompanyName;
                        const prodCompanyLogo = prod.companyLogoUrl || prod.CompanyLogoUrl || (prodCompany ? companyLogos[prodCompany.trim().toLowerCase()] : '');
                        const displayOrderVal = prod.displayOrder || prod.DisplayOrder;

                        return (
                          <tr key={prod.productId}>
                            <td style={{ textAlign: 'center' }}>
                              <span className="ad-rank-badge">
                                {displayOrderVal && displayOrderVal > 0 ? `#${displayOrderVal}` : '—'}
                              </span>
                            </td>
                            <td>
                              <div className="ad-table-thumb">
                                {firstImage ? (
                                  <img src={resolveImageUrl(firstImage)} alt={prod.productName} />
                                ) : (
                                  <span>🧨</span>
                                )}
                              </div>
                            </td>
                            <td className="ad-cell-name">
                              <strong>{prod.productName}</strong>
                              {(prod.videoUrl || prod.VideoUrl) && (
                                <div>
                                  <button
                                    type="button"
                                    className="ad-badge-video"
                                    onClick={() => setVideoModal({
                                      isOpen: true,
                                      videoUrl: prod.videoUrl || prod.VideoUrl,
                                      productName: prod.productName
                                    })}
                                    title="Click to preview demonstration video"
                                  >
                                    ▶ Video Demo
                                  </button>
                                </div>
                              )}
                            </td>
                            <td>
                              {prodCompany ? (
                                <span className="ad-company-badge">
                                  {prodCompanyLogo ? (
                                    <img
                                      src={resolveImageUrl(prodCompanyLogo)}
                                      alt={prodCompany}
                                      className="ad-company-badge-logo"
                                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : (
                                    <span>🏢</span>
                                  )}
                                  <span>{prodCompany}</span>
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                              )}
                            </td>
                            <td className="ad-cell-desc">
                              {prod.productDescription || <span style={{ color: '#94a3b8' }}>No description</span>}
                            </td>
                            <td className="ad-cell-price">
                              ₹{Number(prod.price).toFixed(2)}
                            </td>
                            <td>
                              <span className={`ad-stock-pill ${prod.quantityAvailable < 10 ? 'low' : ''}`}>
                                {prod.quantityAvailable} units
                              </span>
                            </td>
                            <td>
                              {isMostSale ? (
                                <span className="ad-badge-most-sale">🔥 Top Deal</span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>Standard</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="ad-actions-cell">
                                <button 
                                  className="ad-btn-action edit"
                                  onClick={() => handleEdit(prod)}
                                >
                                  Edit
                                </button>
                                <button 
                                  className="ad-btn-action delete"
                                  onClick={() => handleDelete(prod.productId)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View Card List */}
                <div className="ad-mobile-card-list">
                  {filteredProducts.map((prod: any) => {
                    const isMostSale = String(prod.isMostSaleProduct ?? prod.isMostSale ?? prod.IsMostSaleProduct ?? false) === 'true';
                    const firstImage = (prod.imageUrls && prod.imageUrls.length > 0 ? prod.imageUrls[0] : prod.imageUrl) || prod.productImageUrl;
                    const prodCompany = prod.companyName || prod.CompanyName;
                    const prodCompanyLogo = prod.companyLogoUrl || prod.CompanyLogoUrl || (prodCompany ? companyLogos[prodCompany.trim().toLowerCase()] : '');

                    const displayOrderVal = prod.displayOrder || prod.DisplayOrder;

                    return (
                      <div className="ad-mobile-prod-card" key={prod.productId}>
                        <div className="ad-m-prod-media">
                          {firstImage ? (
                            <img src={resolveImageUrl(firstImage)} alt={prod.productName} />
                          ) : (
                            <span>🧨</span>
                          )}
                          {isMostSale && <span className="ad-m-deal-tag">Top Deal</span>}
                        </div>
                        <div className="ad-m-prod-body">
                          <div className="ad-m-prod-name">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span>{prod.productName}</span>
                              {displayOrderVal && displayOrderVal > 0 && (
                                <span className="ad-rank-badge">#{displayOrderVal}</span>
                              )}
                            </div>
                            {prodCompany && (
                              <div style={{ marginTop: 4 }}>
                                <span className="ad-company-badge">
                                  {prodCompanyLogo ? (
                                    <img
                                      src={resolveImageUrl(prodCompanyLogo)}
                                      alt={prodCompany}
                                      className="ad-company-badge-logo"
                                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : (
                                    <span>🏢</span>
                                  )}
                                  <span>{prodCompany}</span>
                                </span>
                              </div>
                            )}
                            {(prod.videoUrl || prod.VideoUrl) && (
                              <div>
                                <button
                                  type="button"
                                  className="ad-badge-video"
                                  onClick={() => setVideoModal({
                                    isOpen: true,
                                    videoUrl: prod.videoUrl || prod.VideoUrl,
                                    productName: prod.productName
                                  })}
                                >
                                  ▶ Video Demo
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="ad-m-prod-price">₹{Number(prod.price).toFixed(2)}</div>
                          <div className="ad-m-prod-stock">Stock: {prod.quantityAvailable} units available</div>
                          <div className="ad-m-prod-desc">{prod.productDescription || 'No description'}</div>
                          <div className="ad-m-cat-actions">
                            <button className="ad-btn-action edit full" onClick={() => handleEdit(prod)}>Edit</button>
                            <button className="ad-btn-action delete full" onClick={() => handleDelete(prod.productId)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="ad-empty-state">
                <div className="ad-empty-icon">🧨</div>
                <h3>No Products Found</h3>
                <p>{searchQuery || selectedCompany !== 'ALL' ? 'No products match your current filter.' : 'No products found in this category. Click "+ Add New Product" to add one.'}</p>
                {(searchQuery || selectedCompany !== 'ALL') && (
                  <button className="ad-filter-pill" onClick={() => { setSearchQuery(''); setSelectedCompany('ALL'); }}>Clear Filters</button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit Product Slide-over Drawer */}
      {showEdit && editProduct && (
        <EditProductDrawer
          product={editProduct}
          categories={categories}
          companies={companies}
          companyLogos={companyLogos}
          onCompanyAdded={(newComp: string, logoUrl?: string) => {
            setCompanies(prev => Array.from(new Set([...prev, newComp])).sort((a, b) => a.localeCompare(b)));
            if (logoUrl) {
              setCompanyLogos(prev => ({ ...prev, [newComp.trim().toLowerCase()]: logoUrl }));
            }
          }}
          onManageCompanies={() => setShowManageCompanies(true)}
          onClose={() => { setShowEdit(false); setEditProduct(null); }}
          onUpdate={handleUpdate}
        />
      )}

      {/* Manage Companies Modal */}
      <ManageCompaniesModal
        isOpen={showManageCompanies}
        companies={companies}
        companyLogos={companyLogos}
        onClose={() => setShowManageCompanies(false)}
        onCompaniesUpdated={(updatedList, renamed, removed) => {
          setCompanies(updatedList);
          setCompanyLogos({ ...getCompanyLogoMapLocal() });
          if (renamed && selectedCompany.toLowerCase() === renamed.oldName.toLowerCase()) {
            setSelectedCompany(renamed.newName);
          }
          if (removed && selectedCompany.toLowerCase() === removed.toLowerCase()) {
            setSelectedCompany('ALL');
          }
          if (selectedCategory) {
            fetchProducts(selectedCategory);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {confirmDeleteId !== null && (
        <div className="ad-modal-backdrop" onClick={cancelDelete}>
          <div className="ad-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-confirm-icon">⚠️</div>
            <h3>Delete Product?</h3>
            <p>Are you sure you want to permanently delete this product? It will be removed from customer view.</p>
            <div className="ad-confirm-actions">
              <button className="ad-btn-action delete full" onClick={confirmDelete}>Yes, Delete</button>
              <button className="ad-btn-action close full" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Demonstration Video Modal */}
      <ProductVideoModal
        isOpen={videoModal.isOpen}
        onClose={() => setVideoModal({ isOpen: false, videoUrl: '', productName: '' })}
        videoUrl={videoModal.videoUrl}
        productName={videoModal.productName}
      />
    </div>
  );
}

// Edit Product Drawer Component
function EditProductDrawer({ product, categories, companies, companyLogos = {}, onCompanyAdded, onManageCompanies, onClose, onUpdate }: any) {
  const initialCompany = product.companyName || product.CompanyName || '';
  const initialLogo =
    product.companyLogoUrl ||
    product.CompanyLogoUrl ||
    (initialCompany ? (companyLogos[initialCompany.trim().toLowerCase()] || getCompanyLogoLocal(initialCompany)) : '');

  const [name, setName] = useState(product.productName);
  const [desc, setDesc] = useState(product.productDescription || '');
  const [price, setPrice] = useState(product.price);
  const [qty, setQty] = useState(product.quantityAvailable);
  const [imageUrls, setImageUrls] = useState<string[]>(
    product.imageUrls && product.imageUrls.length > 0 
      ? product.imageUrls 
      : (product.imageUrl ? [product.imageUrl] : [])
  );
  const [urlInput, setUrlInput] = useState('');
  const [catId, setCatId] = useState(product.categoryId);
  const [companyName, setCompanyName] = useState<string>(initialCompany);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>(initialLogo || '');
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState<boolean>(false);
  const [isAddingNewCompany, setIsAddingNewCompany] = useState<boolean>(false);
  const [newCompanyInput, setNewCompanyInput] = useState<string>('');
  const [isMostSale, setIsMostSale] = useState<boolean>(
    Boolean(product.isMostSaleProduct ?? product.isMostSale ?? product.IsMostSaleProduct ?? false)
  );
  const [displayOrder, setDisplayOrder] = useState<string>(
    (product.displayOrder || product.DisplayOrder) && (product.displayOrder || product.DisplayOrder) > 0
      ? String(product.displayOrder || product.DisplayOrder)
      : ''
  );
  const [videoUrl, setVideoUrl] = useState(product.videoUrl || product.VideoUrl || '');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSelectCompany = (selected: string) => {
    setCompanyName(selected);
    if (selected) {
      const existingLogo = companyLogos[selected.trim().toLowerCase()] || getCompanyLogoLocal(selected);
      setCompanyLogoUrl(existingLogo || '');
    } else {
      setCompanyLogoUrl('');
    }
  };

  const handleAddCustomCompany = () => {
    const trimmed = newCompanyInput.trim();
    if (!trimmed) return;
    saveCustomCompanyLocal(trimmed, companyLogoUrl.trim() || undefined);
    if (onCompanyAdded) onCompanyAdded(trimmed, companyLogoUrl.trim() || undefined);
    setCompanyName(trimmed);
    setNewCompanyInput('');
    setIsAddingNewCompany(false);
  };

  const handleUploadCompanyLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCompanyLogo(true);
    try {
      const res = await uploadProductImage(file);
      if (res?.path) {
        setCompanyLogoUrl(res.path);
      }
    } catch {
      alert('Failed to upload company logo image. Please try again or paste a logo URL.');
    } finally {
      setUploadingCompanyLogo(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let finalImageUrls = [...imageUrls];
      if (files.length > 0) {
        const uploaded = await uploadProductImages(files, product.productId);
        if (uploaded?.paths && Array.isArray(uploaded.paths)) {
          finalImageUrls = finalImageUrls.concat(uploaded.paths);
        }
      }

      const finalCompanyName = (isAddingNewCompany && newCompanyInput.trim())
        ? newCompanyInput.trim()
        : companyName.trim();
      const finalCompanyLogoUrl = companyLogoUrl.trim();

      if (finalCompanyName) {
        saveCustomCompanyLocal(finalCompanyName, finalCompanyLogoUrl || undefined);
        if (onCompanyAdded) onCompanyAdded(finalCompanyName, finalCompanyLogoUrl || undefined);
      }

      await onUpdate({
        ProductId: product.productId,
        ProductName: name,
        ProductDescription: desc,
        Price: Number(price),
        QuantityAvailable: Number(qty),
        ImageUrls: finalImageUrls,
        CategoryId: Number(catId),
        IsMostSaleProduct: isMostSale,
        VideoUrl: videoUrl.trim() || undefined,
        CompanyName: finalCompanyName || undefined,
        CompanyLogoUrl: finalCompanyName && finalCompanyLogoUrl ? finalCompanyLogoUrl : undefined,
        DisplayOrder: displayOrder ? parseInt(displayOrder, 10) : 0,
      });
    } catch {
      alert('Failed to update product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ad-modal-backdrop" onClick={onClose}>
      <div className="ad-drawer-card wider" onClick={e => e.stopPropagation()}>
        <div className="ad-drawer-header">
          <div className="ad-modal-title-group">
            <span className="ad-modal-tag">EDIT PRODUCT</span>
            <h2>{product.productName}</h2>
          </div>
          <button className="ad-modal-close" onClick={onClose}>✕</button>
        </div>

        <form className="ad-drawer-form" onSubmit={handleSubmit}>
          <div className="ad-form-field">
            <label>Product Title <span className="req">*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              className="ad-input"
              placeholder="e.g. 120 Shots Multi Sky Show"
            />
          </div>

          <div className="ad-form-row-3">
            <div className="ad-form-field">
              <label>Price (₹) <span className="req">*</span></label>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(Number(e.target.value))}
                required
                className="ad-input"
                min="0"
                step="0.01"
              />
            </div>
            <div className="ad-form-field">
              <label>Stock Qty <span className="req">*</span></label>
              <input
                type="number"
                value={qty}
                onChange={e => setQty(Number(e.target.value))}
                required
                className="ad-input"
                min="0"
              />
            </div>
            <div className="ad-form-field">
              <label>Rank in Category</label>
              <input
                type="number"
                value={displayOrder}
                onChange={e => setDisplayOrder(e.target.value)}
                className="ad-input"
                min="1"
                placeholder="e.g. 1 (1st)..."
              />
              <span className="ad-field-hint">Rank 1 displays first</span>
            </div>
          </div>

          <div className="ad-form-row-2">
            <div className="ad-form-field">
              <label>Category <span className="req">*</span></label>
              <select
                className="ad-select"
                value={catId ?? ''}
                onChange={e => setCatId(Number(e.target.value))}
                required
              >
                {categories.map((cat: any) => (
                  <option key={cat.categoryID} value={cat.categoryID}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="ad-form-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                <label style={{ marginBottom: 0 }}>Company / Brand</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    className="ad-company-toggle-btn"
                    onClick={() => {
                      setIsAddingNewCompany(prev => !prev);
                      setNewCompanyInput('');
                    }}
                  >
                    {isAddingNewCompany ? 'Select List' : '+ Add New'}
                  </button>
                  {onManageCompanies && (
                    <button
                      type="button"
                      className="ad-company-toggle-btn secondary"
                      onClick={onManageCompanies}
                      title="Modify or remove company names & logos from list"
                    >
                      ⚙️ Modify / Remove
                    </button>
                  )}
                </div>
              </div>
              {!isAddingNewCompany ? (
                <select
                  className="ad-select"
                  value={companyName}
                  onChange={e => {
                    if (e.target.value === '__ADD_NEW__') {
                      setIsAddingNewCompany(true);
                      setNewCompanyInput('');
                    } else {
                      handleSelectCompany(e.target.value);
                    }
                  }}
                >
                  <option value="">-- Select Company / Brand --</option>
                  {(companies || []).map((comp: string, idx: number) => (
                    <option key={idx} value={comp}>
                      {comp}
                    </option>
                  ))}
                  <option value="__ADD_NEW__">+ Type / Add New Company Name...</option>
                </select>
              ) : (
                <div className="ad-company-new-row">
                  <input
                    type="text"
                    value={newCompanyInput}
                    onChange={e => setNewCompanyInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomCompany();
                      }
                    }}
                    className="ad-input"
                    placeholder="Type new company name..."
                    autoFocus
                  />
                  <button
                    type="button"
                    className="ad-btn-add-url"
                    onClick={handleAddCustomCompany}
                  >
                    Add
                  </button>
                </div>
              )}

              {(companyName || isAddingNewCompany) && (
                <div className="ad-company-logo-box">
                  <label className="ad-company-logo-label">
                    Company Logo <span className="ad-field-optional">(Upload image or paste URL)</span>
                  </label>
                  <div className="ad-company-logo-controls">
                    <div className="ad-company-logo-preview-mini">
                      {companyLogoUrl.trim() ? (
                        <img
                          src={resolveImageUrl(companyLogoUrl)}
                          alt="Company Logo"
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span>🏢</span>
                      )}
                    </div>
                    <input
                      type="text"
                      value={companyLogoUrl}
                      onChange={e => setCompanyLogoUrl(e.target.value)}
                      className="ad-input"
                      placeholder="https://example.com/company-logo.png"
                    />
                    <label className="ad-company-upload-label">
                      {uploadingCompanyLogo ? 'Uploading...' : '📤 Upload Logo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadCompanyLogo}
                        disabled={uploadingCompanyLogo}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {companyLogoUrl && (
                      <button
                        type="button"
                        className="ad-btn-action close"
                        onClick={() => setCompanyLogoUrl('')}
                        title="Clear logo"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="ad-form-field">
            <label className="ad-checkbox-label">
              <input
                type="checkbox"
                checked={isMostSale}
                onChange={e => setIsMostSale(e.target.checked)}
              />
              <span>Mark as <strong>Top Deal / Most Sale</strong></span>
            </label>
          </div>

          <div className="ad-form-field">
            <label>Product Description</label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              className="ad-input textarea"
              rows={3}
              placeholder="Features, shot count, effect descriptions..."
            />
          </div>

          <div className="ad-form-field">
            <label>
              YouTube Demonstration Video Link <span className="ad-field-optional">(Optional)</span>
            </label>
            <div className="ad-yt-input-wrap">
              <span className="ad-yt-badge">YouTube</span>
              <input
                type="url"
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                className="ad-input ad-input-yt"
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              />
            </div>
            <span className="ad-field-hint">
              Link a YouTube video or Shorts demonstrating the firecracker in action.
            </span>
            {videoUrl.trim() && isValidYouTubeUrl(videoUrl) && (
              <div className="ad-yt-preview-box">
                <span>✓ Valid YouTube Video recognized (Video ID: {getYouTubeVideoId(videoUrl)})</span>
              </div>
            )}
            {videoUrl.trim() && !isValidYouTubeUrl(videoUrl) && (
              <span className="ad-field-warning">
                ⚠️ Please enter a valid YouTube video link (e.g. https://www.youtube.com/watch?v=... or https://youtu.be/...)
              </span>
            )}
          </div>

          <div className="ad-form-field">
            <label>Product Images</label>
            
            {/* Gallery of existing images */}
            {imageUrls.length > 0 && (
              <div className="ad-gallery-strip">
                {imageUrls.map((u, idx) => (
                  <div key={idx} className="ad-gallery-item">
                    <img src={resolveImageUrl(u)} alt={`Thumbnail ${idx + 1}`} />
                    <button 
                      type="button" 
                      className="ad-gallery-remove-btn"
                      onClick={() => setImageUrls(prev => prev.filter((_, i) => i !== idx))}
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="ad-upload-input-wrap">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={e => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                className="ad-file-input"
              />
            </div>
            {files.length > 0 && (
              <span className="ad-files-selected-text">{files.length} new image file(s) selected</span>
            )}

            <div className="ad-url-add-row">
              <input
                type="text"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                className="ad-input"
                placeholder="Or paste external image URL and click Add"
              />
              <button 
                type="button" 
                className="ad-btn-add-url"
                onClick={() => {
                  if (urlInput.trim()) {
                    setImageUrls(prev => [...prev, urlInput.trim()]);
                    setUrlInput('');
                  }
                }}
              >
                + Add URL
              </button>
            </div>
          </div>

          <div className="ad-drawer-actions">
            <button className="ad-btn-action complete full" type="submit" disabled={saving}>
              {saving ? 'Saving Changes...' : 'Save & Update Product'}
            </button>
            <button className="ad-btn-action close full" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}